import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HandHeart, PawPrint, House, Heart, CheckCircle, X, Smiley, SmileyMeh, Clock, MapPin, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WigglezMark } from "@/components/WigglezMark";

const HERO = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/1dceb75f75b3340c8ae483c30ff137873e1faa66c158c79722ab9618963c52a2.jpeg";

const HOME_TYPES = [
  { key: "house-yard", label: "House w/ yard" },
  { key: "apartment", label: "Apartment" },
  { key: "acreage", label: "Acreage / ranch" },
];
const EXPERIENCE = [
  { key: "first-time", label: "First-time foster" },
  { key: "some", label: "Some experience" },
  { key: "experienced", label: "Seasoned w/ pits" },
];
const MOODS = [
  { key: "thriving", label: "Thriving", icon: Smiley, color: "#ccff00" },
  { key: "settling", label: "Settling in", icon: SmileyMeh, color: "#00e5e5" },
  { key: "needs-help", label: "Needs help", icon: Clock, color: "#ff1493" },
];

export default function FosterPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rescues, setRescues] = useState([]);
  const [stats, setStats] = useState(null);
  const [stories, setStories] = useState([]);
  const [mine, setMine] = useState([]);
  const [applyFor, setApplyFor] = useState(null);
  const [form, setForm] = useState({ home_type: "house-yard", experience: "first-time", phone: "", note: "" });
  const [checkinFor, setCheckinFor] = useState(null);
  const [checkin, setCheckin] = useState({ mood: "thriving", note: "" });
  const [busy, setBusy] = useState(false);

  const loadMine = () => { if (user) api.get("/foster/me").then(({ data }) => setMine(data)).catch(() => {}); };
  const loadStats = () => api.get("/foster/stats").then(({ data }) => setStats(data)).catch(() => {});

  useEffect(() => {
    api.get("/rescues").then(({ data }) => setRescues(data)).catch(() => {});
    api.get("/foster/stories").then(({ data }) => setStories(data)).catch(() => {});
    loadStats();
    loadMine();
    // eslint-disable-next-line
  }, [user]);

  const openApply = (dog) => {
    if (!user) { toast("Sign in to open a foster placement 🐾"); navigate("/auth"); return; }
    setForm({ home_type: "house-yard", experience: "first-time", phone: "", note: "" });
    setApplyFor(dog);
  };

  const submitApply = async () => {
    setBusy(true);
    try {
      await api.post("/foster/apply", {
        dog_id: applyFor.id, dog_name: applyFor.name, dog_image: applyFor.image || "",
        dog_city: applyFor.city || "", home_type: form.home_type, experience: form.experience,
        phone: form.phone, note: form.note,
      });
      toast.success(`You've opened your home to ${applyFor.name} 🐾`);
      setApplyFor(null);
      loadMine(); loadStats();
    } catch { toast.error("Couldn't submit — try again"); }
    finally { setBusy(false); }
  };

  const submitCheckin = async () => {
    setBusy(true);
    try {
      await api.post("/foster/checkin", { application_id: checkinFor.id, mood: checkin.mood, note: checkin.note });
      toast.success(`Check-in logged for ${checkinFor.dog_name} ✅`);
      setCheckinFor(null); setCheckin({ mood: "thriving", note: "" });
      loadMine(); loadStats();
    } catch { toast.error("Couldn't log check-in — try again"); }
    finally { setBusy(false); }
  };

  const STAT_CELLS = stats ? [
    { n: stats.active_fosters, l: "Active fosters" },
    { n: stats.active_placements, l: "Pits in care" },
    { n: stats.checkins, l: "Check-ins logged" },
    { n: "$3M", l: "Ranch goal" },
  ] : [];

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="foster-portal-page">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={HERO} alt="Foster a rescued pit" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute -right-24 top-1/3 h-[30rem] w-[30rem] rounded-full soul-pulse pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,229,229,0.4) 0%, rgba(0,229,229,0) 70%)", filter: "blur(12px)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-background/60" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-12">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="overline text-primary mb-3 inline-flex items-center gap-2"><HandHeart weight="fill" size={15} /> The Foster Portal · Practice What We Preach</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl max-w-3xl">
            Open your <span className="neon-text">home.</span> Save a <span style={{ color: "#ccff00" }}>life.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12 }} className="text-muted-foreground mt-4 max-w-xl">
            The digital foster network that runs the rescue machine. Take a pit in, log a daily check-in, and every placement fuels the $3M ranch. This isn't just a shop — it's a functioning rescue.
          </motion.p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        {/* Stats tied to the ranch */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14" data-testid="foster-stats">
            {STAT_CELLS.map((s) => (
              <div key={s.l} className="text-center rounded-2xl border border-white/10 py-7 bg-card" data-testid={`foster-stat-${s.l}`}>
                <p className="font-display font-black text-4xl sm:text-5xl neon-text">{s.n}</p>
                <p className="overline text-muted-foreground mt-2">{s.l}</p>
              </div>
            ))}
          </div>
        )}

        {/* Your pack in care */}
        {user && mine.length > 0 && (
          <section className="mb-16" data-testid="foster-my-pack">
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl mb-6">Your pack in care.</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mine.map((m) => (
                <div key={m.id} className="rounded-2xl overflow-hidden border border-primary/25 bg-[#0a0a0a]" data-testid={`foster-care-${m.id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {m.dog_image && <img src={m.dog_image} alt={m.dog_name} className="h-full w-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <span className="absolute top-3 left-3 overline px-3 py-1 rounded-full bg-primary/90 text-primary-foreground">Active placement</span>
                    <h3 className="absolute bottom-3 left-4 font-display font-black uppercase text-2xl">{m.dog_name}</h3>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-muted-foreground mb-4">
                      {m.checkins?.length ? `${m.checkins.length} check-in${m.checkins.length > 1 ? "s" : ""} logged` : "No check-ins yet — start today."}
                    </p>
                    {m.checkins?.[0] && (
                      <div className="mb-4 text-xs rounded-lg border border-white/10 p-3 bg-white/5" data-testid={`foster-lastcheckin-${m.id}`}>
                        <span className="overline text-secondary">Last check-in · {m.checkins[0].mood}</span>
                        {m.checkins[0].note && <p className="text-muted-foreground mt-1">"{m.checkins[0].note}"</p>}
                      </div>
                    )}
                    <button onClick={() => { setCheckin({ mood: "thriving", note: "" }); setCheckinFor(m); }}
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[10px] font-black uppercase text-black hover:opacity-90 transition-opacity" style={{ background: "#ccff00" }}
                      data-testid={`foster-checkin-btn-${m.id}`}>
                      <CheckCircle weight="fill" size={17} /> Daily Check-in
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Dogs waiting */}
        <section className="mb-16" data-testid="foster-available">
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl mb-2">Waiting for a foster.</h2>
          <p className="text-muted-foreground mb-6 max-w-xl">Every one of these pits needs a bridge home while they wait. Be the bridge.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="foster-dog-grid">
            {rescues.map((d) => (
              <motion.div key={d.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a] hover:border-primary/50 transition-colors" data-testid={`foster-dog-${d.id}`}>
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={d.image} alt={d.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  {d.urgency === "urgent" && <span className="absolute top-3 right-3 almost-gone overline px-3 py-1 rounded-full bg-red-600 text-white">Urgent</span>}
                  <div className="absolute bottom-3 left-3 px-2 py-1 rounded-md bg-black/55 backdrop-blur-sm border border-white/10"><WigglezMark size="text-[11px]" /></div>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-black uppercase text-2xl">{d.name}</h3>
                    <span className="overline text-muted-foreground">{d.age}</span>
                  </div>
                  <p className="overline text-secondary mt-0.5">{d.breed} · {d.city}</p>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{d.story}</p>
                  <button onClick={() => openApply(d)} className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[10px] font-modern bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid={`foster-apply-btn-${d.id}`}>
                    <HandHeart weight="fill" size={17} /> Foster {d.name}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Success stories */}
        {stories.length > 0 && (
          <section data-testid="foster-stories">
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl mb-2">The wins we celebrate.</h2>
            <p className="text-muted-foreground mb-6 max-w-xl">Every foster success gets its flowers — on the ranch and on the homepage.</p>
            <div className="grid md:grid-cols-3 gap-6">
              {stories.map((s) => (
                <div key={s.id} className="rounded-2xl overflow-hidden border border-secondary/20 bg-[#0a0a0a] neon-glow-aqua" data-testid={`foster-story-${s.id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={s.image} alt={s.dog} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <span className="absolute top-3 left-3 overline px-3 py-1 rounded-full text-black font-semibold" style={{ background: "#ccff00" }}>{s.outcome}</span>
                    <div className="absolute bottom-3 left-4">
                      <h3 className="font-display font-black uppercase text-2xl">{s.dog}</h3>
                      <p className="overline text-white/80">{s.foster} · {s.days} days</p>
                    </div>
                  </div>
                  <div className="p-5"><p className="text-sm text-muted-foreground">{s.story}</p></div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Where they end up — merge with the Ranch */}
        <section className="mt-16 relative rounded-3xl overflow-hidden border border-primary/25" data-testid="foster-ranch-bridge">
          <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_wiggles-unified/artifacts/2pfs8ejt_image-7%20%282%29.jpeg" alt="Heaven Ranch gates" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
          <div className="relative z-10 p-8 sm:p-12 max-w-2xl">
            <p className="overline text-primary mb-3 inline-flex items-center gap-2"><MapPin weight="fill" size={14} /> Foster today · Freedom forever</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl sm:text-5xl mb-4">
              Every foster is a bridge to <span className="neon-text">Heaven.</span>
            </h2>
            <p className="text-muted-foreground mb-7 max-w-lg">
              The dogs you foster don't just wait in limbo — they're on the road to the 300-acre sanctuary we're building. Walk the ranch they're headed to.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/ranch" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="foster-see-ranch">
                <MapPin weight="fill" size={17} /> Walk Heaven Ranch <ArrowRight weight="bold" size={15} />
              </Link>
              <Link to="/donate" className="inline-flex items-center gap-2 border border-secondary/60 text-secondary px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-secondary/10 transition-colors" data-testid="foster-fund-ranch">
                <Heart weight="fill" size={17} /> Fund the sanctuary
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Apply modal */}
      <AnimatePresence>
        {applyFor && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/85 backdrop-blur grid place-items-center p-4" onClick={() => setApplyFor(null)} data-testid="foster-apply-modal">
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              className="w-full max-w-lg rounded-2xl border border-primary/30 bg-[#0b0b0b] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="overline text-primary inline-flex items-center gap-2"><HandHeart weight="fill" size={14} /> Open a foster placement</p>
                  <h3 className="font-display font-black uppercase text-3xl mt-1">Foster {applyFor.name}</h3>
                </div>
                <button onClick={() => setApplyFor(null)} className="text-muted-foreground hover:text-white" data-testid="foster-apply-close"><X size={22} /></button>
              </div>
              <div className="space-y-5">
                <div>
                  <p className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><House size={13} /> Your home</p>
                  <div className="grid grid-cols-3 gap-2">
                    {HOME_TYPES.map((h) => (
                      <button key={h.key} onClick={() => setForm((f) => ({ ...f, home_type: h.key }))}
                        className={`text-xs rounded-[10px] border p-2.5 transition-colors ${form.home_type === h.key ? "border-primary bg-primary/10 text-white" : "border-white/15 text-muted-foreground hover:border-white/30"}`}
                        data-testid={`foster-home-${h.key}`}>{h.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><PawPrint size={13} /> Experience</p>
                  <div className="grid grid-cols-3 gap-2">
                    {EXPERIENCE.map((e) => (
                      <button key={e.key} onClick={() => setForm((f) => ({ ...f, experience: e.key }))}
                        className={`text-xs rounded-[10px] border p-2.5 transition-colors ${form.experience === e.key ? "border-primary bg-primary/10 text-white" : "border-white/15 text-muted-foreground hover:border-white/30"}`}
                        data-testid={`foster-exp-${e.key}`}>{e.label}</button>
                    ))}
                  </div>
                </div>
                <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Phone (so the org can reach you)"
                  className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary transition-colors text-sm" data-testid="foster-phone" />
                <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Anything the pack should know? (other pets, kids, schedule…)" rows={3}
                  className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary transition-colors text-sm resize-none" data-testid="foster-note" />
                <button onClick={submitApply} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] font-black uppercase text-black hover:opacity-90 transition-opacity disabled:opacity-50" style={{ background: "#ccff00" }}
                  data-testid="foster-apply-submit">
                  <Heart weight="fill" size={18} /> {busy ? "Opening your home…" : `Take ${applyFor.name} in`}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Check-in modal */}
      <AnimatePresence>
        {checkinFor && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/85 backdrop-blur grid place-items-center p-4" onClick={() => setCheckinFor(null)} data-testid="foster-checkin-modal">
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              className="w-full max-w-md rounded-2xl border border-secondary/30 bg-[#0b0b0b] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="overline text-secondary inline-flex items-center gap-2"><CheckCircle weight="fill" size={14} /> Daily check-in</p>
                  <h3 className="font-display font-black uppercase text-3xl mt-1">How's {checkinFor.dog_name}?</h3>
                </div>
                <button onClick={() => setCheckinFor(null)} className="text-muted-foreground hover:text-white" data-testid="foster-checkin-close"><X size={22} /></button>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {MOODS.map((m) => (
                  <button key={m.key} onClick={() => setCheckin((c) => ({ ...c, mood: m.key }))}
                    className={`flex flex-col items-center gap-1.5 rounded-[10px] border p-3 transition-colors ${checkin.mood === m.key ? "border-white bg-white/10" : "border-white/15 hover:border-white/30"}`}
                    data-testid={`foster-mood-${m.key}`}>
                    <m.icon weight="fill" size={26} style={{ color: m.color }} />
                    <span className="text-xs text-white">{m.label}</span>
                  </button>
                ))}
              </div>
              <textarea value={checkin.note} onChange={(e) => setCheckin((c) => ({ ...c, note: e.target.value }))} placeholder="Today's note — eating well, big walk, met the neighbors…" rows={3}
                className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-secondary transition-colors text-sm resize-none mb-4" data-testid="foster-checkin-note" />
              <button onClick={submitCheckin} disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] font-modern bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                data-testid="foster-checkin-submit">
                <CheckCircle weight="fill" size={18} /> {busy ? "Logging…" : "Log check-in"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
